/**
 * subcategorias.js — helpers puros para el árbol categoría > subcategoría.
 */

import { escapeHtml } from "./sanitize.js";

export const SIN_SUBCATEGORIA = "none";

/**
 * Subcategorías de una categoría con la cantidad de artículos de cada una, más el
 * grupo "sin subcategoría" (solo si la categoría tiene subcategorías y hay artículos sueltos).
 * Devuelve [{ id, nombre, count }]; vacío si la categoría no tiene subcategorías.
 */
export function subcategoriasConConteo(categoria, articulos) {
  const subs = categoria?.subcategorias || [];
  if (!subs.length) return [];
  const deCategoria = articulos.filter(a => a.categoriaId === categoria.id);
  const items = subs.map(s => ({
    id: s.id,
    nombre: s.nombre,
    count: deCategoria.filter(a => a.subcategoriaId === s.id).length,
  }));
  const sueltos = deCategoria.filter(a => !a.subcategoriaId).length;
  if (sueltos > 0) items.push({ id: SIN_SUBCATEGORIA, nombre: "(sin subcategoría)", count: sueltos });
  return items;
}

/** Nombre a mostrar del filtro de subcategoría activo, o "" si no hay. */
export function nombreSubcategoria(categoria, subId) {
  if (!subId) return "";
  if (subId === SIN_SUBCATEGORIA) return "Sin subcategoría";
  return (categoria?.subcategorias || []).find(s => s.id === subId)?.nombre || "";
}

/** Nombre de la subcategoría de un artículo, o "" si no tiene (o la categoría no la conoce). */
export function subcategoriaDeArticulo(categoria, articulo) {
  if (!articulo?.subcategoriaId) return "";
  return (categoria?.subcategorias || []).find(s => s.id === articulo.subcategoriaId)?.nombre || "";
}

/**
 * Ruta "Categoría / Subcategoría" como HTML escapado, para acompañar al título del
 * artículo (los títulos se escriben pensando que ya se está dentro de la subcategoría).
 */
export function rutaArticuloHtml(catNombre, subNombre) {
  const cat = escapeHtml(catNombre || "");
  return subNombre
    ? `${cat}<span class="kb-ruta-sep">/</span><span class="kb-ruta-sub">${escapeHtml(subNombre)}</span>`
    : cat;
}

/** Mueve el elemento `index` una posición (dir -1 arriba, +1 abajo); devuelve un array nuevo. */
export function moverItem(lista, index, dir) {
  const destino = index + dir;
  if (destino < 0 || destino >= lista.length) return lista;
  const copia = [...lista];
  [copia[index], copia[destino]] = [copia[destino], copia[index]];
  return copia;
}

/**
 * Ordena los artículos para el panel de administración: por categoría (en el orden de
 * `categorias`), dentro de cada una por subcategoría (en su `orden`, los sueltos al final)
 * y luego por título. Devuelve un array nuevo.
 */
export function ordenarPorCategoria(articulos, categorias) {
  const posCat = new Map(categorias.map((c, i) => [c.id, i]));
  const posSub = new Map();
  categorias.forEach(c => (c.subcategorias || []).forEach((s, i) => posSub.set(s.id, i)));
  const pos = (mapa, id) => (mapa.has(id) ? mapa.get(id) : Infinity);
  return [...articulos].sort((a, b) =>
    (pos(posCat, a.categoriaId) - pos(posCat, b.categoriaId) || 0) ||
    (pos(posSub, a.subcategoriaId) - pos(posSub, b.subcategoriaId) || 0) ||
    (a.titulo || "").localeCompare(b.titulo || "", "es", { sensitivity: "base" })
  );
}

/**
 * Filtro del listado de artículos del panel. `filtro` = { cat, sub, texto }: `cat` vacío es
 * "todas", `sub` vacío es "todas" y SIN_SUBCATEGORIA son los artículos sin subcategoría.
 */
export function coincideFiltroArticulo(articulo, { cat = "", sub = "", texto = "" } = {}) {
  if (cat && articulo.categoriaId !== cat) return false;
  if (cat && sub) {
    if (sub === SIN_SUBCATEGORIA ? articulo.subcategoriaId : articulo.subcategoriaId !== sub) return false;
  }
  const t = texto.trim().toLocaleLowerCase("es");
  return !t || (articulo.titulo || "").toLocaleLowerCase("es").includes(t);
}
